import React, {useState} from 'react';

import {NumberInput, Select} from '@/components/common/Input';
import {getTransactionTypesOptions} from '@/constants/formatters';
import {AfriexTransactionType} from '@/types';
import type {AfriexCurrency, CurrencyLimitSetting} from '@/types/Currency';

interface RetailTransactionLimitConfigProps {
  user: AfriexCurrency['user'];
  onSetLimit: (
    transactionType: AfriexTransactionType,
    limits: CurrencyLimitSetting,
  ) => void;
}

/**
 * Retail (`user`) transaction limits.
 *
 * Unset is a real state, not missing data: the server resolves
 * person -> currency -> config, so a currency naming no threshold falls through
 * to the global default for that type.
 */
const RetailTransactionLimitConfig: React.FC<
  RetailTransactionLimitConfigProps
> = ({user, onSetLimit}) => {
  const [selectedTransactionType, setSelectedTransactionType] =
    useState<AfriexTransactionType>('' as AfriexTransactionType);

  // All four retail types carry a frequency threshold.
  const txTypeOptions = getTransactionTypesOptions().filter((option) =>
    [
      AfriexTransactionType.DEPOSIT,
      AfriexTransactionType.WITHDRAW,
      AfriexTransactionType.TRANSFER,
      AfriexTransactionType.SWAP,
    ].includes(option.value),
  );

  const currentThreshold = selectedTransactionType
    ? user?.[selectedTransactionType]?.limits?.transactionFrequencyThreshold
    : undefined;

  return (
    <div className="space-y-3 mb-4">
      <Select
        className="w-full mt-3"
        options={txTypeOptions}
        value={selectedTransactionType}
        label="Transaction Type"
        placeholder="Select Transaction Type"
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          setSelectedTransactionType(e.target.value as AfriexTransactionType)
        }
      />

      <NumberInput
        id="transactionFrequencyThreshold"
        className="w-full h-8"
        value={currentThreshold ?? ''}
        min={0}
        label="Daily Transaction Count Limit"
        onValueChange={(rawValue) =>
          onSetLimit(selectedTransactionType, {
            transactionFrequencyThreshold: Number(rawValue),
          })
        }
        disabled={!selectedTransactionType}
        placeholder={
          selectedTransactionType
            ? 'Enter Daily Transaction Count Limit'
            : 'Select Transaction Type First'
        }
      />

      {selectedTransactionType && (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {currentThreshold === undefined
            ? `${selectedTransactionType} uses the global default for this currency.`
            : `${selectedTransactionType} is capped at ${currentThreshold} per day. A per-user override still takes precedence.`}
        </p>
      )}
    </div>
  );
};

export default RetailTransactionLimitConfig;
